// PlayerCoachManualEditModal.jsx
import React, { useState, useEffect } from "react";
import { FormInput } from "../../common/FormInput";
import { SimpleDropdown } from "../../common/SimpleDropdown";
import { PLAYER_FIELDS } from "./playerFields";
import { COACH_FIELDS } from "./coachFields";
import adminService from "../../../services/adminService";

// Map raw API user object to form-friendly keys
const mapUserToFormData = (user, fields) => {
  if (!user) return {};

  const mapped = {};
  fields.forEach((field) => {
    const key = field.key;
    let value = "";

    // Handle nested/special keys
    if (key === "teamName") {
      value = user.team?.name || "";
    } else if (key === "email") {
      value = user.email ?? "";
    } else if (key === "seasonYear") {
      value = user.seasonYear || "2025-26";
    } else if (key === "previousTeams") {
      value = Array.isArray(user.previousTeams) ? user.previousTeams.join(", ") : "";
    } else {
      value = user[key] ?? "";
    }

    mapped[key] = value === null ? "" : String(value);
  });

  return mapped;
};

export const PlayerCoachManualEditModal = ({ open, mode, onClose, onSave, userData }) => {
  const fields = mode === "coach" || mode === "scout" ? COACH_FIELDS : PLAYER_FIELDS;
  const title =
    mode === "coach" || mode === "scout"
      ? "COACH/SCOUT PROFILE MANUAL EDIT"
      : "PLAYER PROFILE MANUAL EDIT";

  const isPlayer = mode === "player";
  const [formData, setFormData] = useState({});
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadMsg, setVideoUploadMsg] = useState("");
  const [videos, setVideos] = useState([]);
  const [deletingVideoId, setDeletingVideoId] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [committedToError, setCommittedToError] = useState("");

  // Auto-fill form when userData changes or modal opens
  useEffect(() => {
    if (open && userData) {
      const mapped = mapUserToFormData(userData, fields);
      mapped.committedTo = userData.committedTo || "";
      setFormData(mapped);
      setVideoUploadMsg("");
      setVideos(userData.videos || []);
      setPlayingVideoId(null);
    } else if (open && !userData) {
      // Empty form if no user data
      const empty = fields.reduce((acc, f) => {
        acc[f.key] = "";
        return acc;
      }, {});
      empty.committedTo = "";
      setFormData(empty);
      setVideoUploadMsg("");
      setVideos([]);
      setPlayingVideoId(null);
    }
  }, [open, userData]);

  if (!open) return null;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = () => {
    // Validate committedTo when status is committed
    if (formData.commitmentStatus === "committed" && !formData.committedTo?.trim()) {
      setCommittedToError("'Committed To' is required when status is committed");
      return;
    }
    setCommittedToError("");

    // Transform form data before sending to API
    const payload = { ...formData };

    // Remove teamName - API doesn't accept it
    delete payload.teamName;

    // Clear committedTo if uncommitted
    if (payload.commitmentStatus !== "committed") {
      payload.committedTo = "";
    }

    // Convert previousTeams comma-separated string to array
    if (payload.previousTeams !== undefined) {
      payload.previousTeams = payload.previousTeams
        ? payload.previousTeams.split(",").map((t) => t.trim()).filter(Boolean)
        : [];
    }

    onSave(payload);
    setFormData({});
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userData?._id) return;

    setVideoUploading(true);
    setVideoUploadMsg("");
    try {
      const res = await adminService.uploadUserVideo(userData._id, file);
      const newVideos = res?.videos || res?.data?.videos;
      if (newVideos) {
        setVideos(newVideos);
      }
      setVideoUploadMsg("Video uploaded successfully!");
    } catch (err) {
      setVideoUploadMsg(err.response?.data?.message || "Failed to upload video");
    } finally {
      setVideoUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!userData?._id) return;
    setDeletingVideoId(videoId);
    try {
      await adminService.deleteUserVideo(userData._id, videoId);
      setVideos((prev) => prev.filter((v) => v._id !== videoId));
      if (playingVideoId === videoId) setPlayingVideoId(null);
    } catch (err) {
      setVideoUploadMsg(err.response?.data?.message || "Failed to delete video");
    } finally {
      setDeletingVideoId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-4xl max-h-[90vh] rounded-3xl bg-jp-card border border-jp-line shadow-2xl px-6 py-6 md:px-10 md:py-8 overflow-y-auto">
        <h2 className="text-center text-sm md:text-lg font-semibold text-jp-ink mb-5">
          {title}
        </h2>

        {/* Player info header when editing */}
        {userData && (
          <div className="flex items-center gap-4 mb-5 px-2">
            <img
              src={userData.profileImage || userData.photoUrl || ""}
              alt=""
              className="w-12 h-12 rounded-lg object-cover bg-jp-section"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <p className="text-jp-ink text-sm font-semibold">
                {userData.firstName} {userData.lastName}
              </p>
              <p className="text-jp-muted text-xs">
                {userData.team?.name || ""} {userData.position ? `· ${userData.position}` : ""}
              </p>
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-jp-section border border-jp-line p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field, idx) => (
              <React.Fragment key={`${field.key}-${idx}`}>
                <div className={field.input === "Textarea" ? "md:col-span-2" : ""}>
                  {field.input === "Select" ? (
                    <div>
                      <label className="block text-left text-jp-ink text-sm font-roboto mb-3">{field.field}</label>
                      <SimpleDropdown
                        variant="themed"
                        value={formData[field.key] || ""}
                        onChange={(val) => {
                          setFormData({ ...formData, [field.key]: val });
                          if (field.key === "commitmentStatus") setCommittedToError("");
                        }}
                        options={field.options.map((opt) => ({ value: opt, label: opt }))}
                        placeholder="Select..."
                      />
                    </div>
                  ) : (
                    <FormInput
                      label={field.field}
                      name={field.key}
                      type={field.input === "Number" ? "number" : field.input === "Email Input" ? "email" : "text"}
                      value={formData[field.key] || ""}
                      onChange={handleChange}
                      placeholder={field.example}
                    />
                  )}
                </div>

                {/* Show "Committed To" as a separate grid item next to the dropdown */}
                {field.key === "commitmentStatus" && formData.commitmentStatus === "committed" && (
                  <div>
                    <FormInput
                      label="Committed To *"
                      name="committedTo"
                      type="text"
                      value={formData.committedTo || ""}
                      onChange={handleChange}
                      placeholder="e.g. University of Texas"
                    />
                    {committedToError && (
                      <p className="text-red-600 dark:text-red-400 text-xs mt-1">{committedToError}</p>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Video Upload Section - Only for Players */}
          {isPlayer && userData && (
            <div className="mt-6 pt-5 border-t border-jp-line">
              <label className="block text-jp-ink text-sm font-medium mb-3">Videos</label>

              {/* Videos grid */}
              {videos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {videos.map((video, index) => (
                    <div key={video._id || index} className="relative aspect-video bg-jp-section rounded-lg overflow-hidden group">
                      {playingVideoId === video._id ? (
                        <video
                          src={video.url || video.videoUrl}
                          controls
                          autoPlay
                          className="w-full h-full object-cover"
                          onEnded={() => setPlayingVideoId(null)}
                        />
                      ) : (
                        <>
                          {video.thumbnailUrl ? (
                            <img src={video.thumbnailUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-jp-section" />
                          )}
                          {/* Play button overlay */}
                          <button
                            type="button"
                            onClick={() => setPlayingVideoId(video._id)}
                            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-10 h-10 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M6 4l8 6-8 6V4z"/>
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => handleDeleteVideo(video._id)}
                        disabled={deletingVideoId === video._id}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-600/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                      >
                        {deletingVideoId === video._id ? (
                          <div className="w-3 h-3 border border-jp-line border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <label className={`inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-semibold text-xs uppercase tracking-wide transition-all shadow-lg ${
                videoUploading
                  ? 'bg-jp-section text-jp-muted cursor-not-allowed'
                  : 'bg-cyan-500 text-slate-900 hover:bg-cyan-400 cursor-pointer'
              }`}>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                  disabled={videoUploading}
                />
                {videoUploading && (
                  <div className="w-4 h-4 mr-2 border-2 border-jp-line border-t-transparent rounded-full animate-spin"></div>
                )}
                {videoUploading ? 'UPLOADING...' : 'UPLOAD VIDEO'}
              </label>

              {/* Status message */}
              {videoUploadMsg && (
                <p className={`mt-2 text-xs ${videoUploadMsg.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {videoUploadMsg}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="min-w-[140px] h-10 rounded-full border border-sky-300 bg-transparent text-[12px] font-semibold text-sky-100 hover:bg-sky-900/40"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="min-w-[160px] h-10 rounded-full bg-sky-500 text-[12px] font-semibold text-jp-ink hover:bg-sky-400"
          >
            SAVE CHANGES
          </button>
        </div>
      </div>
    </div>
  );
};
